import * as d3Shape from 'd3-shape';
import React from 'react';
import 'resize-observer-polyfill';

import { IWidgetBlockComponent } from '../types/IWidgetBlock';
import Cone from './components/Cone';
import ConeLabels from './components/ConeLabels';
import Legend from './components/Legend';
import PercentageLabels from './components/PercentageLabels';
import { Container } from './Rose.style';
import { RoseModel, RoseModelWithData } from './types';

const LENGTH_OF_CONE = 180;

const Rose = ({ widgetModel }: IWidgetBlockComponent<RoseModel>) => {
    if (!widgetModel) {
        return <span>Rose is unconfigured</span>;
    }

    const { points, cones, settings, label, label2, multiplicationRatio, unit } = widgetModel as RoseModelWithData;
    const { centerSpace, coneCount, binCount, binStepSize, fontSize, colourScale, percentageLabelAngle, showDatapoints, unitLabel } = settings;

    return (
        <Container>
            <div className='roseRender' style={{ position: 'relative', height: '100%', width: '100%', fontSize: (15 * fontSize) / 100 }}>
                <div>
                    <svg width='100%' height='100%' shapeRendering='geometricPrecision' viewBox='0 0 500 500' style={{ position: 'absolute', left: 0 }}>
                        <g style={{ fontSize: (18 * fontSize) / 100 }}>
                            <text fill='currentColor' x='250' y='20' dominantBaseline='middle' textAnchor='middle'>
                                {label}
                            </text>
                            <text fill='currentColor' x='250' y='60' dominantBaseline='middle' textAnchor='middle'>
                                {label2}
                            </text>
                        </g>
                        <g transform='translate(210, 290)'>
                            <path
                                stroke='#f2f2f2'
                                fill='#f2f2f2'
                                d={
                                    d3Shape.arc<Omit<d3Shape.DefaultArcObject, 'padAngle'>>()({
                                        innerRadius: centerSpace,
                                        outerRadius: LENGTH_OF_CONE,
                                        startAngle: 0,
                                        endAngle: 360,
                                    }) as string
                                }
                            />
                            <ConeLabels coneCount={coneCount} innerEdge={centerSpace} outerEdge={LENGTH_OF_CONE} fontSize={fontSize} />
                            {cones && (
                                <g>
                                    {cones.map((cone) => (
                                        <Cone
                                            key={cone.id}
                                            {...cone}
                                            innerEdge={centerSpace}
                                            outerEdge={LENGTH_OF_CONE}
                                            colorScaleLabel={colourScale}
                                            multiplicationRatio={multiplicationRatio}
                                        />
                                    ))}
                                </g>
                            )}
                            <PercentageLabels
                                percentageLabelAngle={percentageLabelAngle}
                                innerEdge={centerSpace}
                                outerEdge={LENGTH_OF_CONE}
                                fontSize={fontSize}
                                multiplicationRatio={multiplicationRatio}
                            />
                        </g>
                        <g transform='translate(420, 80)'>
                            <Legend
                                height={LENGTH_OF_CONE * 2}
                                binCount={binCount}
                                binStepSize={binStepSize}
                                unitLabel={unitLabel}
                                unit={unit}
                                colorScaleLabel={colourScale}
                            />
                        </g>

                        {/* For development/debugging only, displays each datum with normalized magnitudes */}
                        {showDatapoints && !!points?.length && (
                            <g transform='translate(210, 290)'>
                                {points.map(({ angle, normalizedMagnitude }, i) => (
                                    <g key={i} transform={`rotate(${angle})`}>
                                        <g transform={`translate(0, ${-(LENGTH_OF_CONE * normalizedMagnitude)})`}>
                                            <circle r={4} stroke='black' />
                                        </g>
                                    </g>
                                ))}
                            </g>
                        )}
                    </svg>
                </div>
            </div>
        </Container>
    );
};

export default Rose;
